<template>
  <div class="relative w-full mb-6 sm:mb-8 px-2 sm:px-4">
    <!-- ARIA progress container -->
    <div
      ref="barRef"
      role="progressbar"
      :aria-valuenow="displayProgress"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-label="$t('progressBar.aria', { current: currentStep, total: totalSteps, percent: displayProgress })"
      class="relative w-full h-4 sm:h-6 bg-base-200 rounded-full overflow-hidden shadow-inner">
      <div
        class="h-full transition-[width] duration-700 ease-out-back"
        :class="gradientClasses"
        :style="{ width: `${progress}%` }" />
      <!-- Always-centered percentage label -->
      <div class="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
        <span :class="['text-xs sm:text-sm font-semibold', displayProgress >= 50 ? 'text-white' : 'text-primary']">
          {{ displayProgress }}%
        </span>
      </div>
    </div>

    <!-- Microcopy below the bar -->
    <div class="mt-3 flex justify-center">
      <div
        class="bg-primary text-white text-xs sm:text-sm font-medium px-3 py-2 rounded-xl shadow-md whitespace-nowrap">
        {{ $t(`progressBar.microcopy.${microcopyKey}`) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import party from "party-js";

interface Props {
  progress: number;
  currentStep: number;
  totalSteps: number;
}

const props = defineProps<Props>();
const barRef = ref<HTMLElement | null>(null);
const gradientClasses = "bg-gradient-to-r from-primary to-secondary";

const stepSize = computed(() => 100 / props.totalSteps);
const displayProgress = computed(() => Math.round(props.progress));

const microcopyKey = computed(() => {
  const p = props.progress;
  if (p === 0) return "start";
  if (p < stepSize.value) return "step1";
  if (p < stepSize.value * 2) return "step2";
  if (p < stepSize.value * 3) return "step3";
  if (p < stepSize.value * 4) return "step4";
  if (p < stepSize.value * 5) return "step5";
  if (p < 100) return "almost";
  return "complete";
});

// Confetti when completed
watch(
  () => props.progress,
  p => {
    if (p >= 100 && barRef.value) {
      party.confetti(barRef.value, {
        count: party.variation.range(60, 100),
        size: party.variation.range(0.8, 1.4),
      });
    }
  }
);
</script>

<style scoped>
@keyframes ease-out-back {
  0% {
    transform: scaleX(0);
  }
  80% {
    transform: scaleX(1.1);
  }
  100% {
    transform: scaleX(1);
  }
}
.transition-\[width\] {
  transform-origin: left;
  animation: ease-out-back 0.7s both;
}
</style>
